import React, { useState, useEffect, useRef } from 'react';
import { setItem as setMobileStorageItem } from '../utils/mobileStorage';

interface ApiKeyLoginProps {
    onAuthenticated: (apiKey: string) => void;
}

const STORAGE_KEY = 'omnigate_api_key';

// Particles animation background
const ParticleCanvas: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animId: number;
        const particles: { x: number; y: number; vx: number; vy: number; r: number; alpha: number }[] = [];

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        for (let i = 0; i < 60; i++) {
            particles.push({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                vx: (Math.random() - 0.5) * 0.4,
                vy: (Math.random() - 0.5) * 0.4,
                r: Math.random() * 1.5 + 0.5,
                alpha: Math.random() * 0.5 + 0.1,
            });
        }

        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            for (const p of particles) {
                p.x += p.vx;
                p.y += p.vy;
                if (p.x < 0) p.x = canvas.width;
                if (p.x > canvas.width) p.x = 0;
                if (p.y < 0) p.y = canvas.height;
                if (p.y > canvas.height) p.y = 0;

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(139, 92, 246, ${p.alpha})`;
                ctx.fill();
            }

            // Draw connections
            for (let i = 0; i < particles.length; i++) {
                for (let j = i + 1; j < particles.length; j++) {
                    const dx = particles[i].x - particles[j].x;
                    const dy = particles[i].y - particles[j].y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    if (dist < 100) {
                        ctx.beginPath();
                        ctx.moveTo(particles[i].x, particles[i].y);
                        ctx.lineTo(particles[j].x, particles[j].y);
                        ctx.strokeStyle = `rgba(139, 92, 246, ${0.08 * (1 - dist / 100)})`;
                        ctx.lineWidth = 0.5;
                        ctx.stroke();
                    }
                }
            }

            animId = requestAnimationFrame(draw);
        };
        draw();

        return () => {
            cancelAnimationFrame(animId);
            window.removeEventListener('resize', resize);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}
        />
    );
};

// Pulsing lock icon
const LockIcon: React.FC<{ unlocked?: boolean }> = ({ unlocked }) => (
    <svg
        width="28" height="28" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
        style={{ transition: 'all 0.4s ease' }}
    >
        {unlocked ? (
            <>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 9.9-1" />
            </>
        ) : (
            <>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </>
        )}
    </svg>
);

// Google SVG icon
const GoogleIcon: React.FC = () => (
    <svg width="18" height="18" viewBox="0 0 48 48">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.54 28.59A14.5 14.5 0 0 1 9.5 24c0-1.59.28-3.14.76-4.59l-7.98-6.19A23.99 23.99 0 0 0 0 24c0 3.77.87 7.35 2.56 10.59l7.98-6z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6C6.51 42.62 14.62 48 24 48z"/>
        <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
);

const ApiKeyLogin: React.FC<ApiKeyLoginProps> = ({ onAuthenticated }) => {
    const [apiKey, setApiKey] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'error' | 'success'>('idle');
    const [errorMsg, setErrorMsg] = useState('');
    const [showKey, setShowKey] = useState(false);
    const [shimmer, setShimmer] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // Auto-focus
        setTimeout(() => inputRef.current?.focus(), 300);
    }, []);

    const getApiBase = (): string => {
        if (typeof window !== 'undefined') {
            const savedUrl = localStorage.getItem('omnigate_api_url');
            if (savedUrl) return savedUrl;
            
            // If running in Android via Capacitor
            if ((window as any).Capacitor?.isNativePlatform?.()) {
                return 'https://warm-mayfly-16.telebit.io';
            }

            // If on Telebit or remote, use current origin
            if (window.location.hostname !== 'localhost') {
                return window.location.origin;
            }
        }
        return 'http://localhost:8888';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = apiKey.trim();
        if (!trimmed) {
            setErrorMsg('Wpisz klucz API.');
            setStatus('error');
            return;
        }

        setStatus('loading');
        setErrorMsg('');

        try {
            const base = getApiBase();

            // Test 1: /v1/models — wymaga autoryzacji
            const resp = await fetch(`${base}/v1/models`, {
                headers: {
                    'Authorization': `Bearer ${trimmed}`,
                    'X-API-Key': trimmed,
                },
                signal: AbortSignal.timeout(8000),
            });

            if (resp.ok) {
                // Klucz prawidłowy
                setStatus('success');
                setShimmer(true);
                
                // Save to both localStorage and Capacitor Preferences (for mobile)
                localStorage.setItem(STORAGE_KEY, trimmed);
                localStorage.setItem('omnigate_admin_key', trimmed);
                
                // Also save via Capacitor Preferences for Android
                setMobileStorageItem(STORAGE_KEY, trimmed).catch(err => {
                    console.warn('[ApiKeyLogin] Failed to save to Capacitor:', err);
                });
                setMobileStorageItem('omnigate_admin_key', trimmed).catch(err => {
                    console.warn('[ApiKeyLogin] Failed to save admin key to Capacitor:', err);
                });

                setTimeout(() => {
                    onAuthenticated(trimmed);
                }, 900);
            } else if (resp.status === 401) {
                setStatus('error');
                setErrorMsg('Nieprawidłowy klucz API. Sprawdź i spróbuj ponownie.');
            } else {
                setStatus('error');
                setErrorMsg(`Błąd serwera: HTTP ${resp.status}. Spróbuj ponownie.`);
            }
        } catch (err: any) {
            if (err?.name === 'TimeoutError' || err?.name === 'AbortError') {
                setStatus('error');
                setErrorMsg('Przekroczono czas połączenia. Sprawdź czy serwer działa.');
            } else {
                setStatus('error');
                setErrorMsg('Nie można połączyć z serwerem. Sprawdź połączenie.');
            }
        }
    };

    const handleGoogleLogin = () => {
        setGoogleLoading(true);
        const base = getApiBase();
        // Redirect to Google OAuth endpoint
        window.location.href = `${base}/v1/auth/google`;
    };

    const isLoading = status === 'loading';
    const isSuccess = status === 'success';

    return (
        <div style={{
            minHeight: '100vh',
            background: 'radial-gradient(ellipse at 60% 20%, rgba(88,28,135,0.18) 0%, transparent 60%), #0b0d14',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
            position: 'relative',
            overflow: 'hidden',
        }}>
            <ParticleCanvas />

            {/* Glow blobs */}
            <div style={{
                position: 'fixed', top: '10%', left: '15%', width: 400, height: 400,
                borderRadius: '50%', background: 'rgba(139,92,246,0.06)',
                filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0,
            }} />
            <div style={{
                position: 'fixed', bottom: '10%', right: '10%', width: 300, height: 300,
                borderRadius: '50%', background: 'rgba(99,102,241,0.07)',
                filter: 'blur(60px)', pointerEvents: 'none', zIndex: 0,
            }} />

            {/* Card */}
            <div style={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                maxWidth: 420,
                margin: '0 16px',
                background: shimmer
                    ? 'linear-gradient(135deg, rgba(139,92,246,0.25), rgba(99,102,241,0.15), rgba(20,18,30,0.98))'
                    : 'rgba(15,13,24,0.92)',
                border: `1px solid ${isSuccess ? 'rgba(139,92,246,0.6)' : status === 'error' ? 'rgba(239,68,68,0.4)' : 'rgba(139,92,246,0.2)'}`,
                borderRadius: 20,
                padding: '40px 36px 36px',
                backdropFilter: 'blur(20px)',
                boxShadow: isSuccess
                    ? '0 0 60px rgba(139,92,246,0.25), 0 20px 60px rgba(0,0,0,0.5)'
                    : '0 20px 60px rgba(0,0,0,0.5)',
                transition: 'border-color 0.3s, box-shadow 0.5s, background 0.5s',
            }}>

                {/* Logo / Icon */}
                <div style={{ textAlign: 'center', marginBottom: 28 }}>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 64,
                        height: 64,
                        borderRadius: 16,
                        background: isSuccess
                            ? 'linear-gradient(135deg, #7c3aed, #4f46e5)'
                            : 'rgba(139,92,246,0.12)',
                        border: `1.5px solid ${isSuccess ? 'rgba(139,92,246,0.8)' : 'rgba(139,92,246,0.3)'}`,
                        color: isSuccess ? '#fff' : '#a78bfa',
                        marginBottom: 16,
                        transition: 'all 0.4s ease',
                        boxShadow: isSuccess ? '0 0 30px rgba(124,58,237,0.5)' : 'none',
                    }}>
                        <LockIcon unlocked={isSuccess} />
                    </div>

                    <h1 style={{
                        margin: 0,
                        fontSize: 22,
                        fontWeight: 800,
                        letterSpacing: '-0.5px',
                        background: 'linear-gradient(135deg, #e2e8f0, #a78bfa)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}>
                        OmniGate
                    </h1>
                    <p style={{
                        margin: '6px 0 0',
                        fontSize: 12,
                        color: '#64748b',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                    }}>
                        AI Gateway V3 · Secure Access
                    </p>
                </div>

                {/* Divider */}
                <div style={{
                    height: 1,
                    background: 'linear-gradient(to right, transparent, rgba(139,92,246,0.2), transparent)',
                    marginBottom: 28,
                }} />

                {/* Form */}
                <form onSubmit={handleSubmit} noValidate>
                    <label style={{
                        display: 'block',
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#64748b',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        marginBottom: 8,
                    }}>
                        Klucz API
                    </label>

                    <div style={{ position: 'relative', marginBottom: 8 }}>
                        <input
                            ref={inputRef}
                            id="api-key-input"
                            type={showKey ? 'text' : 'password'}
                            value={apiKey}
                            onChange={e => {
                                setApiKey(e.target.value);
                                if (status === 'error') { setStatus('idle'); setErrorMsg(''); }
                            }}
                            onKeyDown={e => e.key === 'Enter' && handleSubmit(e as any)}
                            placeholder="Wklej swój klucz API..."
                            autoComplete="off"
                            spellCheck={false}
                            disabled={isLoading || isSuccess}
                            style={{
                                width: '100%',
                                padding: '13px 44px 13px 14px',
                                fontSize: 13,
                                fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                                fontWeight: 500,
                                letterSpacing: showKey ? '0.02em' : '0.15em',
                                background: 'rgba(255,255,255,0.04)',
                                border: `1.5px solid ${status === 'error' ? 'rgba(239,68,68,0.5)'
                                        : status === 'success' ? 'rgba(139,92,246,0.6)'
                                            : 'rgba(255,255,255,0.1)'
                                    }`,
                                borderRadius: 10,
                                color: '#e2e8f0',
                                outline: 'none',
                                transition: 'border-color 0.2s, background 0.2s',
                                boxSizing: 'border-box',
                            }}
                            onFocus={e => {
                                if (status !== 'error' && status !== 'success') {
                                    e.currentTarget.style.borderColor = 'rgba(139,92,246,0.5)';
                                    e.currentTarget.style.background = 'rgba(139,92,246,0.05)';
                                }
                            }}
                            onBlur={e => {
                                if (status !== 'error' && status !== 'success') {
                                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                                    e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                                }
                            }}
                        />
                        {/* Show/hide toggle */}
                        <button
                            type="button"
                            onClick={() => setShowKey(v => !v)}
                            disabled={isLoading || isSuccess}
                            style={{
                                position: 'absolute',
                                right: 12,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#475569',
                                padding: 4,
                                display: 'flex',
                                alignItems: 'center',
                                transition: 'color 0.2s',
                            }}
                            title={showKey ? 'Ukryj klucz' : 'Pokaż klucz'}
                        >
                            {showKey ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                </svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                </svg>
                            )}
                        </button>
                    </div>

                    {/* Error message */}
                    <div style={{
                        minHeight: 24,
                        marginBottom: 16,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                    }}>
                        {status === 'error' && (
                            <>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5">
                                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                                </svg>
                                <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 500 }}>{errorMsg}</span>
                            </>
                        )}
                        {status === 'success' && (
                            <>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>Autoryzacja pomyślna! Ładowanie...</span>
                            </>
                        )}
                    </div>

                    {/* Submit button */}
                    <button
                        id="login-submit-btn"
                        type="submit"
                        disabled={isLoading || isSuccess || !apiKey.trim()}
                        style={{
                            width: '100%',
                            padding: '13px',
                            fontSize: 13,
                            fontWeight: 700,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            border: 'none',
                            borderRadius: 10,
                            cursor: isLoading || isSuccess || !apiKey.trim() ? 'not-allowed' : 'pointer',
                            background: isSuccess
                                ? 'linear-gradient(135deg, #16a34a, #15803d)'
                                : isLoading
                                    ? 'rgba(139,92,246,0.3)'
                                    : !apiKey.trim()
                                        ? 'rgba(255,255,255,0.06)'
                                        : 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                            color: !apiKey.trim() ? '#475569' : '#fff',
                            transition: 'all 0.25s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            boxShadow: isSuccess
                                ? '0 0 20px rgba(22,163,74,0.3)'
                                : !isLoading && apiKey.trim()
                                    ? '0 4px 20px rgba(124,58,237,0.3)'
                                    : 'none',
                        }}
                    >
                        {isLoading ? (
                            <>
                                <svg style={{ animation: 'spin 1s linear infinite' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                </svg>
                                <span>Weryfikacja...</span>
                            </>
                        ) : isSuccess ? (
                            <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                <span>Zalogowano</span>
                            </>
                        ) : (
                            <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                                    <polyline points="10 17 15 12 10 7" />
                                    <line x1="15" y1="12" x2="3" y2="12" />
                                </svg>
                                <span>Zaloguj się</span>
                            </>
                        )}
                    </button>
                </form>

                {/* ── Divider with "lub" ── */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    margin: '20px 0',
                }}>
                    <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
                    <span style={{ fontSize: 11, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>lub</span>
                    <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
                </div>

                {/* ── Google Login Button ── */}
                <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={googleLoading || isSuccess}
                    style={{
                        width: '100%',
                        padding: '13px',
                        fontSize: 13,
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        border: '1.5px solid rgba(255,255,255,0.12)',
                        borderRadius: 10,
                        cursor: googleLoading || isSuccess ? 'not-allowed' : 'pointer',
                        background: googleLoading
                            ? 'rgba(30,30,40,0.6)'
                            : 'rgba(255,255,255,0.04)',
                        color: '#e2e8f0',
                        transition: 'all 0.25s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                        opacity: isSuccess ? 0.5 : 1,
                    }}
                    onMouseEnter={e => {
                        if (!googleLoading && !isSuccess) {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                            e.currentTarget.style.borderColor = 'rgba(139,92,246,0.3)';
                        }
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.background = googleLoading ? 'rgba(30,30,40,0.6)' : 'rgba(255,255,255,0.04)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)';
                    }}
                >
                    {googleLoading ? (
                        <>
                            <svg style={{ animation: 'spin 1s linear infinite' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                            </svg>
                            <span>Przekierowanie do Google...</span>
                        </>
                    ) : (
                        <>
                            <GoogleIcon />
                            <span>Zaloguj się przez Google</span>
                        </>
                    )}
                </button>

                {/* Footer */}
                <div style={{
                    marginTop: 24,
                    paddingTop: 20,
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    textAlign: 'center',
                }}>
                    <p style={{ margin: 0, fontSize: 11, color: '#334155', lineHeight: 1.6 }}>
                        Klucz API — Recovery Key lub Personal Key<br />
                        <span style={{ color: '#475569' }}>
                            Logowanie przez Google wygeneruje klucz automatycznie
                        </span>
                    </p>
                </div>
            </div>

            <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        * { box-sizing: border-box; }
      `}</style>
        </div>
    );
};

export default ApiKeyLogin;